import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  ClipboardList,
  Download,
  ExternalLink,
  Loader2,
  Mail,
  MessageCircle,
  Phone,
  Plus,
  Truck,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { useFarmaciaAuth } from "../../context/FarmaciaAuthContext";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { farmGet, farmPost } from "./shared";
import { FarmaciaPageChrome } from "../../components/FarmaciaPageChrome";
import { TIPI_STRUTTURA } from "../../config/tipiStruttura";

const FILTERS = [
  { id: "tutte", label: "Tutte" },
  { id: "attive", label: "Attive" },
  { id: "da_verificare", label: "Da verificare" },
  { id: "consegne_imminenti", label: "Consegne imminenti" },
];

const DOC_STATUS = {
  firmato: { label: "Firmato", className: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  in_scadenza: { label: "In scadenza", className: "border-amber-200 bg-amber-50 text-amber-800" },
  da_caricare: { label: "Da caricare", className: "border-sky-200 bg-sky-50 text-sky-700" },
  scaduto: { label: "Scaduto", className: "border-rose-200 bg-rose-50 text-rose-700" },
};

const BAR_COLORS = ["#0F5C4C", "#2563eb", "#7c3aed", "#ea580c", "#dc2626", "#0891b2"];

function initials(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "ST";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function formatShortDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso.includes("T") || iso.includes("-") ? iso : `${iso}T12:00:00`);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return String(iso).slice(0, 10);
  }
}

function relativeDayLabel(iso) {
  if (!iso) return null;
  try {
    const d = new Date(String(iso).slice(0, 10) + "T12:00:00");
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const diff = Math.round((d - today) / 86400000);
    if (diff === 0) return "Oggi";
    if (diff === 1) return "Domani";
    if (diff > 1 && diff <= 14) return `Tra ${diff} giorni`;
    if (diff === -1) return "Ieri";
    if (diff < 0) return `${Math.abs(diff)} gg fa`;
    return null;
  } catch {
    return null;
  }
}

function docBadge(doc) {
  if (!doc || doc.mancante || doc.stato_validita === "mancante") return DOC_STATUS.da_caricare;
  if (doc.stato_validita === "scaduto") return DOC_STATUS.scaduto;
  if (doc.alert_scadenza || doc.stato_validita === "in_scadenza") return DOC_STATUS.in_scadenza;
  if (doc.stato_firma === "firmato" || doc.stato_validita === "valido" || doc.stato_validita === "valido_fino_a_revoca") {
    return DOC_STATUS.firmato;
  }
  if (doc.stato_firma === "da_firmare" || doc.stato_firma === "da_verificare") return DOC_STATUS.in_scadenza;
  return DOC_STATUS.da_caricare;
}

function structureNeedsVerify(docs) {
  if (!docs?.length) return false;
  return docs.some((d) => {
    const b = docBadge(d);
    return b === DOC_STATUS.da_caricare || b === DOC_STATUS.in_scadenza || b === DOC_STATUS.scaduto;
  });
}

function KpiTile({ icon: Icon, label, value, tone }) {
  const tones = {
    teal: "bg-teal-50 text-teal-700",
    sky: "bg-sky-50 text-sky-700",
    amber: "bg-amber-50 text-amber-700",
    violet: "bg-violet-50 text-violet-700",
  };
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs font-medium text-slate-500">{label}</p>
          <p className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">{value}</p>
        </div>
        <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${tones[tone] || tones.teal}`}>
          <Icon className="h-5 w-5" />
        </span>
      </div>
    </div>
  );
}

function StructureDetailPanel({ struttura, docs, loadingDocs, consegna, onClose }) {
  if (!struttura) {
    return (
      <aside className="hidden w-[320px] shrink-0 xl:block">
        <div className="sticky top-0 rounded-2xl border border-dashed border-slate-200 bg-white/60 p-6 text-center">
          <Building2 className="mx-auto mb-2 h-8 w-8 text-slate-300" />
          <p className="text-sm font-medium text-slate-600">Struttura selezionata</p>
          <p className="mt-1 text-xs text-slate-400">Seleziona una riga dall&apos;elenco per il dettaglio.</p>
        </div>
      </aside>
    );
  }

  const nome = struttura.denominazione || struttura.nome || struttura.organization_id;
  const attiva = struttura.stato_collegamento === "attivo";
  const onboardingInAttesa = struttura.onboarding_in_attesa;
  const schedaTo = `/farmacia/struttura/${encodeURIComponent(struttura.organization_id)}`;
  const rel = relativeDayLabel(consegna?.data);

  return (
    <aside className="w-full shrink-0 xl:w-[320px]">
      <div className="sticky top-0 flex max-h-[calc(100vh-10rem)] flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
        <div className="border-b border-slate-100 p-4">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#0F5C4C] text-sm font-semibold text-white">
              {initials(nome)}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <h3 className="truncate text-sm font-semibold text-slate-900">{nome}</h3>
                <button
                  type="button"
                  className="text-xs text-slate-400 hover:text-slate-600 xl:hidden"
                  onClick={onClose}
                >
                  Chiudi
                </button>
              </div>
              <p className="truncate text-xs text-slate-500">
                {[struttura.comune, struttura.indirizzo].filter(Boolean).join(" · ") || "—"}
              </p>
              <Badge
                variant="outline"
                className={`mt-2 text-[10px] ${
                  onboardingInAttesa
                    ? "border-amber-200 bg-amber-50 text-amber-800"
                    : attiva
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-slate-200 text-slate-500"
                }`}
              >
                {onboardingInAttesa ? "In attesa registrazione" : attiva ? "Attiva" : "Cessata"}
              </Badge>
            </div>
          </div>
        </div>

        <div className="space-y-4 overflow-y-auto p-4">
          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Dati principali
            </p>
            <dl className="space-y-1.5 text-xs">
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Ospiti aderenti</dt>
                <dd className="font-semibold text-slate-800">{struttura.ospiti_aderenti ?? 0}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Referente</dt>
                <dd className="truncate text-right font-medium text-slate-800">
                  {struttura.referente || "—"}
                </dd>
              </div>
              {struttura.email ? (
                <div className="flex items-center justify-between gap-2">
                  <dt className="flex items-center gap-1 text-slate-500">
                    <Mail className="h-3 w-3" /> Email
                  </dt>
                  <dd className="truncate text-right text-slate-700">{struttura.email}</dd>
                </div>
              ) : null}
              {struttura.telefono ? (
                <div className="flex items-center justify-between gap-2">
                  <dt className="flex items-center gap-1 text-slate-500">
                    <Phone className="h-3 w-3" /> Telefono
                  </dt>
                  <dd className="font-medium text-slate-800">{struttura.telefono}</dd>
                </div>
              ) : null}
            </dl>
          </div>

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Servizio deblistering
            </p>
            <div className="rounded-xl bg-slate-50 px-3 py-2.5 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Stato</span>
                <span className={onboardingInAttesa ? "font-semibold text-amber-700" : "font-semibold text-emerald-700"}>{onboardingInAttesa ? "In attesa di completamento" : attiva ? "Attivo" : "Non attivo"}</span>
              </div>
              <div className="mt-1.5 flex items-center justify-between">
                <span className="text-slate-500">Ultima attività</span>
                <span className="font-medium text-slate-800">
                  {struttura.ultima_attivita ? formatShortDate(struttura.ultima_attivita) : "—"}
                </span>
              </div>
              <div className="mt-1.5 flex items-center justify-between">
                <span className="text-slate-500">Prossima consegna</span>
                <span className="font-medium text-slate-800">
                  {consegna?.data
                    ? `${formatShortDate(consegna.data)}${rel ? ` · ${rel}` : ""}`
                    : "—"}
                </span>
              </div>
            </div>
          </div>

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Documenti contrattuali
            </p>
            {loadingDocs ? (
              <Loader2 className="h-4 w-4 animate-spin text-slate-300" />
            ) : (
              <ul className="space-y-1.5">
                {(docs || []).map((d) => {
                  const badge = docBadge(d);
                  return (
                    <li
                      key={d.tipo}
                      className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-2.5 py-2 text-xs"
                    >
                      <span className="truncate text-slate-700">{d.tipo_label || d.tipo}</span>
                      <Badge variant="outline" className={`shrink-0 text-[10px] ${badge.className}`}>
                        {badge.label}
                      </Badge>
                    </li>
                  );
                })}
                {!docs?.length ? (
                  <li className="rounded-lg border border-dashed border-slate-200 px-2.5 py-3 text-center text-xs text-slate-400">
                    Nessun documento
                  </li>
                ) : null}
              </ul>
            )}
          </div>

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Riepilogo rapido
            </p>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-xl bg-amber-50 px-1 py-2.5">
                <p className="text-lg font-semibold text-amber-800">{struttura.richieste_aperte ?? 0}</p>
                <p className="text-[10px] text-amber-700/80">Pratiche aperte</p>
              </div>
              <div className="rounded-xl bg-sky-50 px-1 py-2.5">
                <p className="text-lg font-semibold text-sky-800">{struttura.ordini_aperti ?? 0}</p>
                <p className="text-[10px] text-sky-700/80">Ordini extra</p>
              </div>
              <div className="rounded-xl bg-violet-50 px-1 py-2.5">
                <p className="text-lg font-semibold text-violet-800">{struttura.ricette_da_verificare ?? 0}</p>
                <p className="text-[10px] text-violet-700/80">Ricette</p>
              </div>
            </div>
          </div>

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Ultime attività
            </p>
            <ol className="relative space-y-3 border-l border-slate-200 pl-3">
              {struttura.ultima_attivita ? (
                <li className="text-xs text-slate-600">
                  <span className="absolute -left-1 mt-1 h-2 w-2 rounded-full bg-[#0F5C4C]" />
                  Ultima attività registrata
                  <span className="mt-0.5 block text-[10px] text-slate-400">
                    {formatShortDate(struttura.ultima_attivita)}
                  </span>
                </li>
              ) : null}
              {(struttura.richieste_aperte || 0) > 0 ? (
                <li className="text-xs text-slate-600">
                  <span className="absolute -left-1 mt-1 h-2 w-2 rounded-full bg-amber-400" />
                  {struttura.richieste_aperte} richieste / pratiche aperte
                </li>
              ) : null}
              {consegna ? (
                <li className="text-xs text-slate-600">
                  <span className="absolute -left-1 mt-1 h-2 w-2 rounded-full bg-violet-400" />
                  Consegna programmata {formatShortDate(consegna.data)}
                </li>
              ) : null}
              {!struttura.ultima_attivita && !(struttura.richieste_aperte > 0) && !consegna ? (
                <li className="text-xs text-slate-400">Nessuna attività recente</li>
              ) : null}
            </ol>
          </div>
        </div>

        <div className="mt-auto border-t border-slate-100 p-3">
          {onboardingInAttesa ? (
            <p className="text-center text-xs text-slate-500">In attesa che il referente completi la registrazione.</p>
          ) : (
          <Button asChild className="h-10 w-full rounded-xl bg-[#0F5C4C] hover:bg-[#0c4a3e]">
            <Link to={schedaTo}>
              Vai alla scheda struttura
              <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
            </Link>
          </Button>
          )}
        </div>
      </div>
    </aside>
  );
}

export default function FarmaciaStrutture() {
  const { token, hasPermission } = useFarmaciaAuth();
  const [strutture, setStrutture] = useState([]);
  const [docsByOrg, setDocsByOrg] = useState({});
  const [consegne, setConsegne] = useState([]);
  const [kpi, setKpi] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingDocsSel, setLoadingDocsSel] = useState(false);
  const [filter, setFilter] = useState("tutte");
  const [selectedId, setSelectedId] = useState(null);
  const [orgFilter, setOrgFilter] = useState("all");
  const [collegaOpen, setCollegaOpen] = useState(false);
  const [collegaMode, setCollegaMode] = useState("esistente");
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [savingCollegamento, setSavingCollegamento] = useState(false);
  const [newStruttura, setNewStruttura] = useState({
    nome: "", tipo_struttura: "RSA", posti_totali: 1, telefono: "", comune: "", via: "",
    numero_civico: "", provincia: "", cap: "", partita_iva: "", referente_nome: "", referente_email: "",
  });

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const [strRes, kpiRes, consRes] = await Promise.all([
        farmGet("/farmacia/strutture", token),
        farmGet("/farmacia/ricette/kpi", token).catch(() => ({})),
        farmGet("/farmacia/consegne", token, { data: today }).catch(() => ({ items: [] })),
      ]);
      const items = strRes.items || [];
      setStrutture(items);
      setKpi(kpiRes || {});
      setConsegne(consRes.items || consRes.consegne || []);
      setSelectedId((prev) => {
        if (prev && items.some((s) => s.organization_id === prev)) return prev;
        return items[0]?.organization_id || null;
      });

      // Documenti essenziali per badge / alert (strutture tipicamente poche)
      const docsEntries = await Promise.all(
        items.filter((s) => !s.onboarding_in_attesa).slice(0, 20).map(async (s) => {
          try {
            const res = await farmGet(
              `/farmacia/strutture/${encodeURIComponent(s.organization_id)}/documenti-essenziali`,
              token
            );
            return [s.organization_id, res.items || []];
          } catch {
            return [s.organization_id, []];
          }
        })
      );
      setDocsByOrg(Object.fromEntries(docsEntries));
    } catch {
      setStrutture([]);
      toast.error("Impossibile caricare le strutture");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const searchStrutture = async () => {
    if (search.trim().length < 3) {
      toast.error("Inserisci almeno 3 caratteri");
      return;
    }
    try {
      const res = await farmGet("/farmacia/strutture/cerca", token, { q: search.trim() });
      setSearchResults(res.items || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Ricerca strutture non riuscita");
    }
  };

  const collegaEsistente = async (organizationId) => {
    setSavingCollegamento(true);
    try {
      await farmPost("/farmacia/strutture/collega", token, { organization_id: organizationId });
      toast.success("Struttura collegata");
      setCollegaOpen(false);
      await load();
    } catch (error) {
      toast.error(error?.message || error?.response?.data?.detail || "Collegamento non riuscito");
    } finally {
      setSavingCollegamento(false);
    }
  };

  const creaStruttura = async () => {
    setSavingCollegamento(true);
    try {
      const data = await farmPost("/farmacia/strutture/crea", token, {
        ...newStruttura, posti_totali: Number(newStruttura.posti_totali),
      });
      toast.success(data.invito_inviato ? "Struttura creata: invito inviato al referente" : "Struttura creata: invito da reinviare");
      setCollegaOpen(false);
      await load();
    } catch (error) {
      toast.error(error?.message || "Creazione struttura non riuscita");
    } finally {
      setSavingCollegamento(false);
    }
  };

  useEffect(() => {
    if (!token || !selectedId) return;
    if (strutture.find((s) => s.organization_id === selectedId)?.onboarding_in_attesa) return;
    if (docsByOrg[selectedId]) return;
    let cancelled = false;
    setLoadingDocsSel(true);
    farmGet(`/farmacia/strutture/${encodeURIComponent(selectedId)}/documenti-essenziali`, token)
      .then((res) => {
        if (cancelled) return;
        setDocsByOrg((prev) => ({ ...prev, [selectedId]: res.items || [] }));
      })
      .catch(() => {
        if (!cancelled) setDocsByOrg((prev) => ({ ...prev, [selectedId]: [] }));
      })
      .finally(() => {
        if (!cancelled) setLoadingDocsSel(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, selectedId, docsByOrg]);

  const consegneByOrg = useMemo(() => {
    const map = {};
    for (const c of consegne) {
      const oid = c.organization_id || c.organizationId;
      if (!oid) continue;
      const prev = map[oid];
      if (!prev || String(c.data || "") < String(prev.data || "")) map[oid] = c;
    }
    return map;
  }, [consegne]);

  const enriched = useMemo(
    () =>
      strutture.map((s) => {
        const docs = docsByOrg[s.organization_id] || [];
        const needsVerify = structureNeedsVerify(docs);
        const consegna = consegneByOrg[s.organization_id];
        return {
          ...s,
          docs,
          needsVerify,
          consegna,
          hasImminentDelivery: Boolean(consegna),
        };
      }),
    [strutture, docsByOrg, consegneByOrg]
  );

  const filtered = useMemo(() => {
    let list = enriched;
    if (orgFilter !== "all") {
      list = list.filter((s) => s.organization_id === orgFilter);
    }
    if (filter === "attive") list = list.filter((s) => s.stato_collegamento === "attivo");
    if (filter === "da_verificare") list = list.filter((s) => s.needsVerify);
    if (filter === "consegne_imminenti") list = list.filter((s) => s.hasImminentDelivery);
    return list;
  }, [enriched, filter, orgFilter]);

  const selected = enriched.find((s) => s.organization_id === selectedId) || null;
  const canManageStrutture = hasPermission?.("config:manage") !== false;

  const kpiValues = useMemo(() => {
    const attive = strutture.filter((s) => s.stato_collegamento === "attivo").length;
    const ospiti =
      typeof kpi.ospiti_aderenti === "number"
        ? kpi.ospiti_aderenti
        : strutture.reduce((a, s) => a + (s.ospiti_aderenti || 0), 0);
    const pratiche = strutture.reduce((a, s) => a + (s.richieste_aperte || 0), 0);
    const consegneSett = kpi.consegne_programmate ?? consegne.length;
    return { attive, ospiti, pratiche, consegneSett };
  }, [strutture, kpi, consegne]);

  const distribuzione = useMemo(() => {
    const rows = [...strutture]
      .map((s) => ({
        id: s.organization_id,
        nome: s.denominazione || s.nome || s.organization_id,
        n: s.ospiti_aderenti || 0,
      }))
      .sort((a, b) => b.n - a.n)
      .slice(0, 6);
    const max = Math.max(1, ...rows.map((r) => r.n));
    return rows.map((r, i) => ({ ...r, pct: (r.n / max) * 100, color: BAR_COLORS[i % BAR_COLORS.length] }));
  }, [strutture]);

  const alertDocs = useMemo(() => {
    const out = [];
    for (const s of enriched) {
      for (const d of s.docs || []) {
        const b = docBadge(d);
        if (b === DOC_STATUS.in_scadenza || b === DOC_STATUS.scaduto || b === DOC_STATUS.da_caricare) {
          out.push({
            key: `${s.organization_id}-${d.tipo}`,
            struttura: s.denominazione || s.nome,
            organization_id: s.organization_id,
            doc: d.tipo_label || d.tipo,
            badge: b,
          });
        }
      }
    }
    return out.slice(0, 6);
  }, [enriched]);

  const upcomingConsegne = useMemo(() => {
    return [...consegne]
      .filter((c) => c.data)
      .sort((a, b) => String(a.data).localeCompare(String(b.data)))
      .slice(0, 5);
  }, [consegne]);

  const exportCsv = () => {
    const header = ["Nome", "Comune", "Indirizzo", "Ospiti", "Stato", "Richieste aperte", "Referente", "Telefono", "Email"];
    const rows = filtered.map((s) => [
      s.denominazione || s.nome || "",
      s.comune || "",
      s.indirizzo || "",
      s.ospiti_aderenti ?? 0,
      s.stato_collegamento === "attivo" ? "Attiva" : "Cessata",
      s.richieste_aperte ?? 0,
      s.referente || "",
      s.telefono || "",
      s.email || "",
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";"))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `strutture-farmacia-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Esportazione avviata");
  };

  const contactStructure = (s) => {
    if (s.email) {
      window.location.href = `mailto:${s.email}`;
      return;
    }
    if (s.telefono) {
      window.location.href = `tel:${s.telefono}`;
      return;
    }
    toast.message("Nessun contatto disponibile per questa struttura");
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 xl:flex-row xl:items-start">
      <div className="min-w-0 flex-1 space-y-5">
        <FarmaciaPageChrome>
          {canManageStrutture ? <Button
            type="button"
            size="sm"
            className="rounded-xl bg-[#0F5C4C] hover:bg-[#0c4a3e]"
            onClick={() => setCollegaOpen((open) => !open)}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Collega struttura
          </Button> : null}
          <Button type="button" size="sm" variant="outline" className="rounded-xl" onClick={exportCsv}>
            <Download className="mr-1.5 h-4 w-4" />
            Esporta
          </Button>
        </FarmaciaPageChrome>
        {collegaOpen ? (
          <section className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div><h2 className="text-sm font-semibold text-slate-900">Aggiungi struttura</h2><p className="text-xs text-slate-600">Collega una struttura Affidya esistente o crea una nuova struttura con invito al referente.</p></div>
              <Button size="sm" variant="ghost" onClick={() => setCollegaOpen(false)}>Chiudi</Button>
            </div>
            <div className="mt-3 flex w-fit gap-1 rounded-lg bg-white p-1 text-xs">
              <button type="button" onClick={() => setCollegaMode("esistente")} className={`rounded-md px-3 py-1.5 ${collegaMode === "esistente" ? "bg-emerald-700 text-white" : "text-slate-600"}`}>Collega esistente</button>
              <button type="button" onClick={() => setCollegaMode("nuova")} className={`rounded-md px-3 py-1.5 ${collegaMode === "nuova" ? "bg-emerald-700 text-white" : "text-slate-600"}`}>Crea nuova</button>
            </div>
            {collegaMode === "esistente" ? <div className="mt-4">
              <div className="flex gap-2"><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nome, comune o partita IVA" /><Button size="sm" onClick={searchStrutture}>Cerca</Button></div>
              <div className="mt-3 space-y-2">
                {searchResults.map((item) => <div key={item.organization_id} className="flex items-center justify-between gap-3 rounded-xl border border-white bg-white p-3 text-sm"><span><strong>{item.nome}</strong><small className="ml-2 text-slate-500">{[item.comune, item.provincia].filter(Boolean).join(" · ")}</small></span><Button size="sm" variant="outline" disabled={!item.collegabile || savingCollegamento} onClick={() => collegaEsistente(item.organization_id)}>{item.collegabile ? "Collega" : item.motivo_non_collegabile}</Button></div>)}
              </div>
            </div> : <div className="mt-4 grid gap-2 md:grid-cols-3">
              <Input
                placeholder="Nome struttura *"
                value={newStruttura.nome}
                onChange={(e) => setNewStruttura((prev) => ({ ...prev, nome: e.target.value }))}
              />
              <Select
                value={newStruttura.tipo_struttura}
                onValueChange={(value) => setNewStruttura((prev) => ({ ...prev, tipo_struttura: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tipologia *" />
                </SelectTrigger>
                <SelectContent>
                  {TIPI_STRUTTURA.map((tipo) => (
                    <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {[
                ["posti_totali", "Posti totali *"],
                ["telefono", "Telefono *"], ["comune", "Comune *"], ["via", "Via *"], ["numero_civico", "Numero civico *"],
                ["provincia", "Provincia *"], ["cap", "CAP"], ["partita_iva", "Partita IVA"], ["referente_nome", "Referente *"], ["referente_email", "Email referente *"],
              ].map(([key, label]) => (
                <Input
                  key={key}
                  type={key === "posti_totali" ? "number" : key === "referente_email" ? "email" : "text"}
                  placeholder={label}
                  value={newStruttura[key]}
                  onChange={(e) => setNewStruttura((prev) => ({ ...prev, [key]: e.target.value }))}
                />
              ))}
              <div className="md:col-span-3"><Button disabled={savingCollegamento} onClick={creaStruttura}>{savingCollegamento ? "Creazione…" : "Crea e invia invito"}</Button></div>
            </div>}
          </section>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <KpiTile icon={Building2} label="Strutture attive" value={kpiValues.attive} tone="teal" />
          <KpiTile icon={Users} label="Ospiti aderenti" value={kpiValues.ospiti} tone="sky" />
          <KpiTile icon={ClipboardList} label="Pratiche aperte" value={kpiValues.pratiche} tone="amber" />
          <KpiTile icon={Truck} label="Consegne in settimana" value={kpiValues.consegneSett} tone="violet" />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-1.5 rounded-xl bg-slate-100/80 p-1">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${
                  filter === f.id
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <select
            className="h-9 rounded-xl border border-slate-200 bg-white px-3 text-xs text-slate-700"
            value={orgFilter}
            onChange={(e) => setOrgFilter(e.target.value)}
          >
            <option value="all">Tutte le strutture</option>
            {strutture.map((s) => (
              <option key={s.organization_id} value={s.organization_id}>
                {s.denominazione || s.nome}
              </option>
            ))}
          </select>
        </div>

        <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
          {filtered.length === 0 ? (
            <div className="px-4 py-16 text-center">
              <Building2 className="mx-auto mb-3 h-8 w-8 text-slate-300" />
              <p className="text-sm font-medium text-slate-700">Nessuna struttura</p>
              <p className="mt-1 text-xs text-slate-400">
                {strutture.length === 0
                  ? "Chiedi all’amministratore Affidya di collegare le strutture al tuo account."
                  : "Nessun risultato per i filtri selezionati."}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((s) => {
                const nome = s.denominazione || s.nome || s.organization_id;
                const active = selectedId === s.organization_id;
                const statusBadge = s.onboarding_in_attesa
                  ? {
                      label: "In attesa registrazione",
                      className: "border-amber-200 bg-amber-50 text-amber-800",
                    }
                  : s.needsVerify
                  ? {
                      label: "In verifica documenti",
                      className: "border-amber-200 bg-amber-50 text-amber-800",
                    }
                  : s.stato_collegamento === "attivo"
                    ? {
                        label: "Attiva",
                        className: "border-emerald-200 bg-emerald-50 text-emerald-700",
                      }
                    : {
                        label: "Cessata",
                        className: "border-slate-200 bg-slate-50 text-slate-500",
                      };
                const rel = relativeDayLabel(s.consegna?.data);
                return (
                  <li key={s.organization_id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(s.organization_id)}
                      className={`flex w-full flex-col gap-3 px-4 py-3.5 text-left transition hover:bg-slate-50/80 sm:flex-row sm:items-center ${
                        active ? "bg-emerald-50/40" : ""
                      }`}
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0F5C4C] text-xs font-semibold text-white">
                          {initials(nome)}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-900">{nome}</p>
                          <p className="truncate text-xs text-slate-500">
                            {[s.comune, s.indirizzo].filter(Boolean).join(" · ") || "—"}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600 sm:gap-4">
                        <span className="tabular-nums">
                          <span className="font-semibold text-slate-900">{s.ospiti_aderenti ?? 0}</span> ospiti
                        </span>
                        <Badge variant="outline" className={`text-[10px] ${statusBadge.className}`}>
                          {statusBadge.label}
                        </Badge>
                        <span className="tabular-nums">
                          <span className="font-semibold text-slate-900">{s.richieste_aperte ?? 0}</span> pratiche
                        </span>
                        <span className="min-w-[7rem] text-slate-500">
                          {s.consegna?.data ? (
                            <>
                              {formatShortDate(s.consegna.data)}
                              {rel ? (
                                <span className="ml-1 rounded-md bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700">
                                  {rel}
                                </span>
                              ) : null}
                            </>
                          ) : (
                            "—"
                          )}
                        </span>
                        <span className="flex items-center gap-2">
                          <span
                            role="link"
                            tabIndex={0}
                            className="inline-flex items-center gap-1 text-slate-500 hover:text-emerald-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              contactStructure(s);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.stopPropagation();
                                contactStructure(s);
                              }
                            }}
                          >
                            <MessageCircle className="h-3.5 w-3.5" />
                            Contatta
                          </span>
                          <Link
                            to={`/farmacia/struttura/${encodeURIComponent(s.organization_id)}`}
                            className="inline-flex items-center gap-1 font-medium text-emerald-700 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Apri scheda
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                        </span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <div className="grid gap-3 lg:grid-cols-3">
          <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-slate-800">Distribuzione ospiti</h3>
            {distribuzione.length === 0 ? (
              <p className="text-xs text-slate-400">Nessun dato</p>
            ) : (
              <ul className="space-y-2.5">
                {distribuzione.map((r) => (
                  <li key={r.id}>
                    <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                      <span className="truncate text-slate-600">{r.nome}</span>
                      <span className="font-semibold tabular-nums text-slate-800">{r.n}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${r.pct}%`, backgroundColor: r.color }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-slate-800">Consegne programmate</h3>
            {upcomingConsegne.length === 0 ? (
              <p className="text-xs text-slate-400">Nessuna consegna programmata oggi</p>
            ) : (
              <ul className="space-y-2">
                {upcomingConsegne.map((c) => {
                  const rel = relativeDayLabel(c.data);
                  const nome =
                    c.struttura_nome ||
                    strutture.find((s) => s.organization_id === (c.organization_id || c.organizationId))
                      ?.denominazione ||
                    "Struttura";
                  return (
                    <li
                      key={c.id || `${c.organization_id}-${c.data}`}
                      className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-2.5 py-2 text-xs"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-medium text-slate-800">{nome}</p>
                        <p className="text-slate-400">{formatShortDate(c.data)}</p>
                      </div>
                      {rel ? (
                        <Badge variant="outline" className="shrink-0 border-violet-200 text-[10px] text-violet-700">
                          {rel}
                        </Badge>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-slate-800">Alert documenti</h3>
            {alertDocs.length === 0 ? (
              <p className="text-xs text-slate-400">Nessun alert documentale</p>
            ) : (
              <ul className="space-y-2">
                {alertDocs.map((a) => (
                  <li
                    key={a.key}
                    className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-2.5 py-2 text-xs"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium text-slate-800">{a.struttura}</p>
                      <p className="truncate text-slate-400">{a.doc}</p>
                    </div>
                    <Badge variant="outline" className={`shrink-0 text-[10px] ${a.badge.className}`}>
                      {a.badge.label}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {selected ? (
          <div className="xl:hidden">
            <StructureDetailPanel
              struttura={selected}
              docs={selected.docs}
              loadingDocs={loadingDocsSel}
              consegna={selected.consegna}
              onClose={() => setSelectedId(null)}
            />
          </div>
        ) : null}
      </div>

      <div className="hidden xl:block">
        <StructureDetailPanel
          struttura={selected}
          docs={selected?.docs}
          loadingDocs={loadingDocsSel}
          consegna={selected?.consegna}
          onClose={() => setSelectedId(null)}
        />
      </div>
    </div>
  );
}
